import { Button } from '@amglobal/ui-shared'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { InAppNotification } from '../api/notifications-types'
import {
  useInAppNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useUnreadNotificationCountQuery,
} from '../api/use-in-app-notifications'
import { resolveNotificationHref } from '../lib/resolve-notification-href'

type NotificationBellProps = {
  tenantId: string
}

type DesktopPermission = NotificationPermission | 'unsupported'

function readDesktopPermission(): DesktopPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function NotificationBell({ tenantId }: NotificationBellProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [permission, setPermission] = useState<DesktopPermission>(readDesktopPermission)
  const unread = useUnreadNotificationCountQuery(tenantId)
  const list = useInAppNotificationsQuery(tenantId)
  const markRead = useMarkNotificationReadMutation(tenantId)
  const markAll = useMarkAllNotificationsReadMutation(tenantId)
  const count = unread.data ?? 0
  const items = list.data?.items ?? []

  async function enableDesktopAlerts() {
    if (!('Notification' in window)) return
    const next = await Notification.requestPermission()
    setPermission(next)
  }

  function openItem(item: InAppNotification) {
    if (!item.isRead) void markRead.mutateAsync(item.id).catch(() => undefined)
    setOpen(false)
    const href = resolveNotificationHref(item.actionUrl)
    if (href.kind === 'external') {
      window.location.assign(href.url)
      return
    }
    if (href.kind === 'internal') navigate(href.path)
  }

  const label = count > 0 ? `Notifications, ${count} unread` : 'Notifications'

  return (
    <div className="relative">
      <button
        type="button"
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-border-default text-text-secondary hover:bg-surface-workspace"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {count > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-primary px-1 text-[10px] font-semibold text-white">
            {count > 9 ? '9+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Notifications"
            className="absolute right-0 z-50 mt-2 flex max-h-[min(24rem,70vh)] w-80 flex-col overflow-hidden rounded-xl border border-border-default bg-surface-elevated shadow-lg"
          >
            <div className="flex items-center justify-between gap-2 border-b border-border-default px-3 py-2">
              <p className="text-sm font-semibold text-text-primary">Notifications</p>
              {count > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={markAll.isPending}
                  onClick={() => void markAll.mutateAsync().catch(() => undefined)}
                >
                  Mark all read
                </Button>
              ) : null}
            </div>

            {permission === 'default' ? (
              <div className="border-b border-border-default px-3 py-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => void enableDesktopAlerts()}>
                  Enable desktop alerts
                </Button>
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
              {list.isLoading ? (
                <p className="px-3 py-4 text-sm text-text-muted">Loading…</p>
              ) : list.isError ? (
                <p className="px-3 py-4 text-sm text-text-muted">Could not load notifications.</p>
              ) : items.length === 0 ? (
                <p className="px-3 py-4 text-sm text-text-muted">No notifications yet.</p>
              ) : (
                <ul>
                  {items.map((item) => (
                    <li key={item.id} className="border-b border-border-default last:border-b-0">
                      <button
                        type="button"
                        className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-surface-workspace"
                        onClick={() => openItem(item)}
                      >
                        <span className="flex items-start justify-between gap-2">
                          <span
                            className={`text-sm text-text-primary ${item.isRead ? 'font-medium' : 'font-semibold'}`}
                          >
                            {item.title}
                          </span>
                          {!item.isRead ? (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-primary" aria-hidden />
                          ) : null}
                        </span>
                        {item.body ? (
                          <span className="line-clamp-2 text-xs text-text-secondary">{item.body}</span>
                        ) : null}
                        {item.createdAt ? (
                          <span className="text-[11px] text-text-muted">{formatWhen(item.createdAt)}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
